import { getWeekdays } from './weekdays'
import { IAgentSchedule } from './scheduling'
import { agentLiterals } from './literals'
import { formatLiteral } from 'utils/localeText'

const displayKeys = [
  'ag_recurring',
  'ag_hourly',
  't_daily',
  't_weekly',
  't_monthly',
  'ag_minute_number',
  'ag_monthly_timing',
] as const
type ScheduleLabels = Record<typeof displayKeys[number], string>
const defaultLabels = Object.fromEntries(displayKeys.map((key) => [key, agentLiterals[key].fallback])) as ScheduleLabels

export function getSidebarSchedule(
  schedule: IAgentSchedule,
  offset: number | null,
  labels: ScheduleLabels = defaultLabels,
  locale = 'en'
) {
  const daysOfWeek = Object.fromEntries(getWeekdays(locale).map(({ value, short }) => [value, short]))
  const fallback = {
    label: schedule.is_one_time ? '' : labels.ag_recurring,
    timing: schedule.description || schedule.cron,
    order: 4,
    time: Infinity,
    due: Infinity,
  }
  const parts = schedule.cron.trim().split(/\s+/)
  if (parts.length !== 5) return fallback
  const [minute, hour, day, month, weekday] = parts
  const numeric = (value: string, max: number, min = 0) => /^\d+$/.test(value) && +value >= min && +value <= max
  if (!numeric(minute, 59)) return fallback
  if (!schedule.is_one_time && hour === '*' && day === '*' && month === '*' && weekday === '*') {
    return {
      ...fallback,
      label: labels.ag_hourly,
      timing: formatLiteral(labels.ag_minute_number, { minute: minute.padStart(2, '0') }),
      order: -1,
      time: +minute,
    }
  }
  if (!numeric(hour, 23)) return fallback

  // Old schedules (tz_name=null) stored cron in UTC — convert to local for display.
  // New schedules (tz_name set) store cron in local time already.
  let displayHour = +hour
  let displayMinute = +minute
  if (schedule.tz_name === null && offset != null) {
    const totalMinutes = displayHour * 60 + displayMinute + Math.round(offset * 60)
    displayHour = ((Math.floor(totalMinutes / 60) % 24) + 24) % 24
    displayMinute = ((totalMinutes % 60) + 60) % 60
  }

  const time = displayHour * 60 + displayMinute
  const clock = `${String(displayHour).padStart(2, '0')}:${String(displayMinute).padStart(2, '0')}`
  if (schedule.is_one_time) {
    // Cron has no year. Only the form's explicit date establishes that a job is past.
    const date = schedule.description?.match(/^Once on (\d{2})\/(\d{2})\/(\d{4}) at (\d{2}):(\d{2})$/)
    if (!date || offset == null || !numeric(day, 31, 1) || !numeric(month, 12, 1)) return fallback
    const [, dd, mm, yyyy, hh, min] = date
    if (+dd !== +day || +mm !== +month || +hh !== +hour || +min !== +minute) return fallback
    const local = new Date(Date.UTC(+yyyy, +mm - 1, +dd, +hh, +min))
    if (local.getUTCMonth() !== +mm - 1 || local.getUTCDate() !== +dd) return fallback
    return { ...fallback, timing: `${dd}/${mm}/${yyyy} · ${clock}`, due: local.getTime() - offset * 3600000 }
  }
  if (month !== '*') return fallback
  if (day === '*' && weekday === '*') return { ...fallback, label: labels.t_daily, timing: clock, order: 0, time }
  if (day === '*' && (weekday === '1-5' || weekday.split(',').every((value) => numeric(value, 6)))) {
    const days =
      weekday === '1-5'
        ? `${daysOfWeek[1]}–${daysOfWeek[5]}`
        : weekday
            .split(',')
            .map((value) => daysOfWeek[+value])
            .join(', ')
    return { ...fallback, label: labels.t_weekly, timing: `${days} · ${clock}`, order: 1, time }
  }
  if (numeric(day, 31, 1) && weekday === '*')
    return {
      ...fallback,
      label: labels.t_monthly,
      timing: formatLiteral(labels.ag_monthly_timing, { day: +day, time: clock }),
      order: 2,
      time,
    }
  return fallback
}
